// pages/sellerApplications/SellerApplications.js - COMPLETE UPDATED
import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  Spinner,
  Alert,
  Badge,
  Row,
  Col,
  Card,
  Form,
} from "react-bootstrap";
import {
  FaFileAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEye,
  FaStore,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaCheck,
  FaTimes,
  FaUsers,
  FaLink,
  FaCopy,
  FaFileUpload,
  FaExclamationTriangle,
  FaSync,
  FaKey,
  FaInfoCircle,
  FaGlobe,
} from "react-icons/fa";
import axios from "axios";
import Header from "../../component/header/header";
import Sidebar from "../../component/sidebar/sidebar";
import "./SellerApplications.css";

 const API_URL = "https://api.native91.com/api";
 const SELLER_API_URL = "https://api-admin.native91.com/api/seller";

//const API_URL = "http://localhost:9000/api";
//const SELLER_API_URL = "http://localhost:5177/api/seller";

const SellerApplications = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [syncingDocuments, setSyncingDocuments] = useState(false);

  const [showDetail, setShowDetail] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [approveNotes, setApproveNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [documentLoading, setDocumentLoading] = useState(false);

  const [documentLink, setDocumentLink] = useState("");
  const [documentTrackingId, setDocumentTrackingId] = useState("");
  const [documentStatus, setDocumentStatus] = useState("");

  const token = localStorage.getItem("token");

  // ================= FETCH APPLICATIONS =================
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/sellers/applications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const apps = res.data.applications || [];
      setApplications(apps);
      setFilteredApps(apps);
      setError("");
      
      await fetchDocumentStatuses(apps);
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Session expired. Please login again.");
      } else if (err.code === "ERR_NETWORK") {
        setError("Network error. Please check your connection.");
      } else {
        setError(err.response?.data?.message || "Failed to fetch applications");
      }
    } finally {
      setLoading(false);
    }
  };

  // ================= FETCH DOCUMENT STATUSES =================
  const fetchDocumentStatuses = async (apps) => {
    if (!apps || apps.length === 0) return;
    
    setSyncingDocuments(true);
    
    try {
      const updatedApps = await Promise.all(
        apps.map(async (app) => {
          try {
            const docRes = await axios.get(
              `${SELLER_API_URL}/documents/email/${app.email}`
            );
            if (docRes.data.success && docRes.data.document) {
              return {
                ...app,
                documentStatus: docRes.data.document.status,
                documentTrackingId: docRes.data.document.trackingId,
                documentExists: true
              };
            }
          } catch (docErr) {
            return {
              ...app,
              documentStatus: null,
              documentTrackingId: null,
              documentExists: false
            };
          }
          return app;
        })
      );
      
      setApplications(updatedApps);
      setFilteredApps(updatedApps);
    } catch (err) {
      console.error("Error fetching document statuses:", err);
    } finally {
      setSyncingDocuments(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  // ================= FILTER APPLICATIONS =================
  useEffect(() => {
    let filtered = applications;

    if (statusFilter !== "all") {
      filtered = filtered.filter((app) => app.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (app) =>
          app.fullName?.toLowerCase().includes(term) ||
          app.email?.toLowerCase().includes(term) ||
          app.businessName?.toLowerCase().includes(term) ||
          app.trackingId?.toLowerCase().includes(term)
      );
    }

    setFilteredApps(filtered);
  }, [applications, statusFilter, searchTerm]);

  // ================= SYNC DOCUMENT STATUSES =================
  const syncDocumentStatuses = async () => {
    await fetchDocumentStatuses(applications);
    setSuccess("Document statuses synced successfully!");
    setTimeout(() => setSuccess(""), 3000);
  };

  // ================= CREATE DOCUMENT LINK =================
  const createDocumentLink = async (app) => {
    setDocumentLoading(true);
    setError("");

    try {
      const checkRes = await axios.get(
        `${SELLER_API_URL}/documents/email/${app.email}`
      );

      if (checkRes.data.success && checkRes.data.document) {
        const doc = checkRes.data.document;
        const link = `${window.location.origin}/document-upload/${doc.trackingId}`;
        setDocumentLink(link);
        setDocumentTrackingId(doc.trackingId);
        setDocumentStatus(doc.status);
        setSelectedApp(app);
        setShowDocumentModal(true);
        setDocumentLoading(false);
        
        const updatedApps = applications.map(a => 
          a._id === app._id ? { ...a, documentStatus: doc.status, documentTrackingId: doc.trackingId, documentExists: true } : a
        );
        setApplications(updatedApps);
        setFilteredApps(updatedApps);
        return;
      }
    } catch (err) {
      if (err.response?.status === 404) {
        try {
          // ✅ Pass website when creating document
          const createRes = await axios.post(
            `${SELLER_API_URL}/documents/create`,
            {
              email: app.email,
              company: app.businessName,
              website: app.website || "" // ✅ Include website
            }
          );

          if (createRes.data.success) {
            const trackingId = createRes.data.trackingId;
            const link = `${window.location.origin}/document-upload/${trackingId}`;
            
            setDocumentLink(link);
            setDocumentTrackingId(trackingId);
            setDocumentStatus("draft");
            setSelectedApp(app);
            setShowDocumentModal(true);
            
            const updatedApps = applications.map(a => 
              a._id === app._id ? { ...a, documentStatus: 'draft', documentTrackingId: trackingId, documentExists: true } : a
            );
            setApplications(updatedApps);
            setFilteredApps(updatedApps);
            
            try {
              await navigator.clipboard.writeText(link);
              setSuccess("Document link created and copied to clipboard!");
              setTimeout(() => setSuccess(""), 5000);
            } catch (clipErr) {}
          }
        } catch (createErr) {
          setError(createErr.response?.data?.message || "Failed to create document link");
        }
      } else if (err.code === "ERR_NETWORK") {
        setError("Network error. Please check if the seller backend is running.");
      } else {
        setError(err.response?.data?.message || "Failed to check existing document");
      }
    } finally {
      setDocumentLoading(false);
    }
  };

  const copyLinkToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(documentLink);
      setSuccess("Link copied to clipboard!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Failed to copy link. Please select and copy manually.");
    }
  };

  const sendLinkViaEmail = async () => {
    if (!selectedApp || !documentTrackingId) return;

    setDocumentLoading(true);
    try {
      const response = await axios.post(
        `${SELLER_API_URL}/documents/send-link`,
        {
          email: selectedApp.email,
          trackingId: documentTrackingId,
          company: selectedApp.businessName,
        }
      );

      if (response.data.success) {
        setSuccess(`Document link generated! ${response.data.emailSent ? 'Email sent to ' + selectedApp.email : 'Please copy the link manually'}`);
        setTimeout(() => setSuccess(""), 6000);
      } else {
        setSuccess(`Document link generated! Please copy the link manually.`);
        setTimeout(() => setSuccess(""), 5000);
      }
    } catch (err) {
      setSuccess(`Document link generated! Please copy the link manually.`);
      setTimeout(() => setSuccess(""), 5000);
    } finally {
      setDocumentLoading(false);
    }
  };

  const handleViewDetail = (app) => {
    setSelectedApp(app);
    setShowDetail(true);
  };

  // ================= APPROVE APPLICATION =================
  const handleApprove = async () => {
    if (!selectedApp) return;

    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/sellers/applications/${selectedApp._id}/approve`,
        { notes: approveNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let docTrackingId = selectedApp.documentTrackingId;
      
      if (!docTrackingId) {
        try {
          const checkDocRes = await axios.get(
            `${SELLER_API_URL}/documents/email/${selectedApp.email}`
          );
          if (checkDocRes.data.success && checkDocRes.data.document) {
            docTrackingId = checkDocRes.data.document.trackingId;
          } else {
            const createDocRes = await axios.post(
              `${SELLER_API_URL}/documents/create`,
              {
                email: selectedApp.email,
                company: selectedApp.businessName,
                website: selectedApp.website || "" // ✅ Include website
              }
            );
            if (createDocRes.data.success) {
              docTrackingId = createDocRes.data.trackingId;
            }
          }
        } catch (docErr) {
          console.error("Error creating/finding document:", docErr);
        }
      }

      try {
        await axios.post(
          `${SELLER_API_URL}/send-approval-email`,
          {
            email: selectedApp.email,
            name: selectedApp.fullName,
            company: selectedApp.businessName,
            vendorId: res.data.vendorId || "Vendor ID will be sent after document verification",
            trackingId: docTrackingId
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setSuccess(`✅ ${selectedApp.businessName} approved! Document submission link sent to ${selectedApp.email}!`);
      } catch (emailErr) {
        console.error("Email error:", emailErr);
        setSuccess(`✅ ${selectedApp.businessName} approved! (Email notification failed - please send document link manually)`);
      }

      const updatedApps = applications.map(app => 
        app._id === selectedApp._id ? { 
          ...app, 
          status: 'approved', 
          vendorId: res.data.vendorId,
          documentTrackingId: docTrackingId || app.documentTrackingId
        } : app
      );
      setApplications(updatedApps);
      setFilteredApps(updatedApps);

      setShowApproveModal(false);
      setApproveNotes("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to approve application");
    } finally {
      setActionLoading(false);
    }
  };

  // ================= REJECT APPLICATION =================
  const handleReject = async () => {
    if (!selectedApp) return;

    if (!rejectReason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/sellers/applications/${selectedApp._id}/reject`,
        { reason: rejectReason, notes: rejectNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      try {
        await axios.post(
          `${SELLER_API_URL}/send-rejection-email`,
          {
            email: selectedApp.email,
            name: selectedApp.fullName,
            company: selectedApp.businessName,
            reason: rejectReason
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess(`✅ ${selectedApp.businessName} rejected and email sent to ${selectedApp.email}!`);
      } catch (emailErr) {
        console.error("Email error:", emailErr);
        setSuccess(`✅ ${selectedApp.businessName} rejected! (Email notification failed)`);
      }

      const updatedApps = applications.map(app => 
        app._id === selectedApp._id ? { ...app, status: 'rejected', rejectionReason: rejectReason } : app
      );
      setApplications(updatedApps);
      setFilteredApps(updatedApps);

      setShowRejectModal(false);
      setRejectReason("");
      setRejectNotes("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reject application");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <Badge bg="warning" className="px-3 py-2">
            <FaClock className="me-1" /> Pending
          </Badge>
        );
      case "approved":
        return (
          <Badge bg="success" className="px-3 py-2">
            <FaCheckCircle className="me-1" /> Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge bg="danger" className="px-3 py-2">
            <FaTimesCircle className="me-1" /> Rejected
          </Badge>
        );
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getDocumentStatusBadge = (status) => {
    if (!status) {
      return <Badge bg="secondary">Not Created</Badge>;
    }
    switch (status) {
      case "verified":
        return <Badge bg="success">Verified</Badge>;
      case "submitted":
        return <Badge bg="warning">Submitted</Badge>;
      case "rejected":
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">Draft</Badge>;
    }
  };

  const getStats = () => {
    const total = applications.length;
    const pending = applications.filter((a) => a.status === "pending").length;
    const approved = applications.filter((a) => a.status === "approved").length;
    const rejected = applications.filter((a) => a.status === "rejected").length;
    return { total, pending, approved, rejected };
  };

  const stats = getStats();

  if (loading) {
    return (
      <>
        <Header />
        <Sidebar />
        <main className="admin-content mt-5">
          <Container>
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading applications...</p>
            </div>
          </Container>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Sidebar />
      <main className="admin-content mt-5">
        <Container fluid>
          {error && (
            <Alert variant="danger" onClose={() => setError("")} dismissible>
              <FaExclamationTriangle className="me-2" />
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" onClose={() => setSuccess("")} dismissible>
              {success}
            </Alert>
          )}

          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4>
              <FaFileAlt className="me-2" />
              Seller Applications
              <Badge bg="secondary" className="ms-2">
                {filteredApps.length}
              </Badge>
            </h4>
            <Button
              variant="outline-info"
              size="sm"
              onClick={syncDocumentStatuses}
              disabled={syncingDocuments}
            >
              <FaSync className={syncingDocuments ? "fa-spin" : ""} /> 
              {syncingDocuments ? "Syncing..." : "Sync Document Status"}
            </Button>
          </div>

          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center shadow-sm">
                <Card.Body>
                  <h6 className="text-muted">Total</h6>
                  <h3>{stats.total}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center shadow-sm border-warning">
                <Card.Body>
                  <h6 className="text-muted">Pending</h6>
                  <h3 className="text-warning">{stats.pending}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center shadow-sm border-success">
                <Card.Body>
                  <h6 className="text-muted">Approved</h6>
                  <h3 className="text-success">{stats.approved}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center shadow-sm border-danger">
                <Card.Body>
                  <h6 className="text-muted">Rejected</h6>
                  <h3 className="text-danger">{stats.rejected}</h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={8}>
              <Form.Control
                type="text"
                placeholder="Search by name, email, business, or tracking ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-100"
              />
            </Col>
            <Col md={4}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </Form.Select>
            </Col>
          </Row>

          <div className="table-responsive">
            <Table bordered hover className="applications-table">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Tracking ID</th>
                  <th>Business Name</th>
                  <th>Applicant</th>
                  <th>Email</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Documents</th>
                  <th>Registered</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApps.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-5 text-muted">
                      <FaUsers size={40} className="mb-3" />
                      <p>No applications found</p>
                    </td>
                  </tr>
                ) : (
                  filteredApps.map((app, index) => {
                    return (
                      <tr key={app._id}>
                        <td>{index + 1}</td>
                        <td>
                          <code className="fw-bold">{app.trackingId}</code>
                        </td>
                        <td>
                          <FaStore className="me-2 text-primary" />
                          {app.businessName}
                        </td>
                        <td>{app.fullName}</td>
                        <td>
                          <FaEnvelope className="me-1 text-muted" />
                          {app.email}
                        </td>
                        <td>
                          <Badge bg="info">{app.category}</Badge>
                        </td>
                        <td>{getStatusBadge(app.status)}</td>
                        <td>
                          {getDocumentStatusBadge(app.documentStatus)}
                        </td>
                        <td>
                          {app.registeredAt
                            ? new Date(app.registeredAt).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-1"
                            onClick={() => handleViewDetail(app)}
                            title="View Details"
                          >
                            <FaEye />
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline-info"
                            className="me-1"
                            onClick={() => createDocumentLink(app)}
                            disabled={documentLoading}
                            title="Create/View Document Link"
                          >
                            <FaFileUpload />
                          </Button>
                          
                          {app.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                variant="outline-success"
                                className="me-1"
                                onClick={() => {
                                  setSelectedApp(app);
                                  setShowApproveModal(true);
                                }}
                                title="Approve"
                              >
                                <FaCheck />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => {
                                  setSelectedApp(app);
                                  setShowRejectModal(true);
                                }}
                                title="Reject"
                              >
                                <FaTimes />
                              </Button>
                            </>
                          )}
                          {app.status !== "pending" && (
                            <Badge bg="secondary">Reviewed</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Container>
      </main>

      {/* ==================== DETAIL MODAL - FIXED WEBSITE ==================== */}
      <Modal show={showDetail} onHide={() => setShowDetail(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFileAlt className="me-2" />
            Application Details
          </Modal.Title>
        </Modal.Header>
        {selectedApp && (
          <Modal.Body>
            <Row>
              <Col md={6}>
                <div className="detail-item">
                  <label>Application ID</label>
                  <p><code className="fw-bold">{selectedApp.trackingId}</code></p>
                </div>
                <div className="detail-item">
                  <label>Full Name</label>
                  <p>{selectedApp.fullName}</p>
                </div>
                <div className="detail-item">
                  <label>Email Address</label>
                  <p><FaEnvelope className="me-1 text-muted" /> {selectedApp.email}</p>
                </div>
                <div className="detail-item">
                  <label>Phone Number</label>
                  <p><FaPhone className="me-1 text-muted" /> {selectedApp.phoneNumber || "N/A"}</p>
                </div>
                <div className="detail-item">
                  <label>Business Name</label>
                  <p><FaStore className="me-1 text-primary" /> {selectedApp.businessName}</p>
                </div>
              </Col>
              <Col md={6}>
                <div className="detail-item">
                  <label>Category</label>
                  <p><Badge bg="info">{selectedApp.category}</Badge></p>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <p>{getStatusBadge(selectedApp.status)}</p>
                </div>
                <div className="detail-item">
                  <label>Document Status</label>
                  <p>{getDocumentStatusBadge(selectedApp.documentStatus)}</p>
                </div>
                <div className="detail-item">
                  <label>Registered On</label>
                  <p><FaCalendarAlt className="me-1 text-muted" /> {new Date(selectedApp.registeredAt).toLocaleString()}</p>
                </div>
                <div className="detail-item">
                  <label>Last Updated</label>
                  <p><FaClock className="me-1 text-muted" /> {new Date(selectedApp.updatedAt).toLocaleString()}</p>
                </div>
                {/* ✅ FIXED: Show actual website from application data */}
                <div className="detail-item">
                  <label>Website</label>
                  <p>
                    <FaGlobe className="me-1 text-primary" />
                    {selectedApp.website ? (
                      <a 
                        href={selectedApp.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#007bff', textDecoration: 'none' }}
                      >
                        {selectedApp.website}
                      </a>
                    ) : (
                      <span className="text-muted">N/A</span>
                    )}
                  </p>
                </div>
              </Col>
            </Row>

            {/* Tracking Token */}
            {selectedApp.trackingToken && (
              <Alert variant="secondary" className="mt-2">
                <h6><FaKey className="me-2" /> Tracking Token</h6>
                <p className="mb-0" style={{ wordBreak: 'break-all', fontSize: '12px' }}>
                  <code>{selectedApp.trackingToken}</code>
                </p>
                {selectedApp.trackingTokenExpires && (
                  <p className="mb-0 mt-1 text-muted small">
                    <strong>Expires:</strong> {new Date(selectedApp.trackingTokenExpires).toLocaleString()}
                  </p>
                )}
              </Alert>
            )}

            {/* Document Tracking ID */}
            {selectedApp.documentTrackingId && (
              <Alert variant="info" className="mt-2">
                <h6>📄 Document Tracking ID</h6>
                <p className="mb-0">
                  <code>{selectedApp.documentTrackingId}</code>
                </p>
              </Alert>
            )}

            {/* Admin Notes */}
            {selectedApp.adminNotes && (
              <Alert variant="light" className="mt-2">
                <h6>📝 Admin Notes</h6>
                <p className="mb-0">{selectedApp.adminNotes}</p>
              </Alert>
            )}

            {/* Rejection Reason */}
            {selectedApp.status === "rejected" && selectedApp.rejectionReason && (
              <Alert variant="danger" className="mt-2">
                <h6>❌ Rejection Reason</h6>
                <p className="mb-0">{selectedApp.rejectionReason}</p>
              </Alert>
            )}

            {/* Vendor Info (if approved) */}
            {selectedApp.status === "approved" && (
              <Alert variant="success" className="mt-2">
                <h6>✅ Application Approved</h6>
                <p className="mb-0">
                  <strong>Email:</strong> {selectedApp.email}
                </p>
                {selectedApp.vendorId && (
                  <p className="mb-0 mt-1">
                    <strong>Vendor ID:</strong> <code>{selectedApp.vendorId}</code>
                  </p>
                )}
                {selectedApp.reviewedAt && (
                  <p className="mb-0 mt-1 text-muted small">
                    <strong>Reviewed On:</strong> {new Date(selectedApp.reviewedAt).toLocaleString()}
                  </p>
                )}
                <p className="mb-0 mt-2 text-muted">
                  <small>
                    🔐 Login credentials will be sent to the vendor after document verification.
                  </small>
                </p>
              </Alert>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetail(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ==================== DOCUMENT LINK MODAL ==================== */}
      <Modal show={showDocumentModal} onHide={() => setShowDocumentModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title><FaLink className="me-2 text-primary" /> Document Upload Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApp && (
            <>
              <div className="mb-3">
                <h6>Seller Information</h6>
                <p className="mb-1"><strong>Business:</strong> {selectedApp.businessName}</p>
                <p className="mb-1"><strong>Email:</strong> {selectedApp.email}</p>
                <p className="mb-1"><strong>Tracking ID:</strong> <code>{documentTrackingId}</code></p>
                <p><strong>Document Status:</strong> {getDocumentStatusBadge(documentStatus)}</p>
              </div>

              <Alert variant="info">
                <h6>📄 Document Upload Link</h6>
                <div className="d-flex align-items-center gap-2 mt-2">
                  <code className="flex-grow-1 bg-light p-2 rounded" style={{ wordBreak: 'break-all' }}>
                    {documentLink}
                  </code>
                </div>
                <div className="mt-2 d-flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline-primary" onClick={copyLinkToClipboard}>
                    <FaCopy className="me-1" /> Copy Link
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-success"
                    onClick={sendLinkViaEmail}
                    disabled={documentLoading}
                  >
                    <FaEnvelope className="me-1" /> Send via Email
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => { window.open(documentLink, '_blank'); }}
                  >
                    <FaEye className="me-1" /> Preview
                  </Button>
                </div>
              </Alert>

              <Alert variant="warning" className="mt-3">
                <h6>📝 Instructions</h6>
                <ul className="mb-0">
                  <li>Share this unique link with the seller</li>
                  <li>Seller can upload documents without logging in</li>
                  <li>All data is auto-saved as they fill the form</li>
                  <li>🔐 Login credentials will be sent after document verification</li>
                </ul>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDocumentModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ==================== APPROVE MODAL ==================== */}
      <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title><FaCheckCircle className="me-2 text-success" /> Approve Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApp && (
            <>
              <p><strong>Business:</strong> {selectedApp.businessName}</p>
              <p><strong>Applicant:</strong> {selectedApp.fullName}</p>
              <p><strong>Email:</strong> {selectedApp.email}</p>
              <p><strong>Document Status:</strong> {getDocumentStatusBadge(selectedApp.documentStatus)}</p>
              <Form.Group className="mt-3">
                <Form.Label>Admin Notes (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Add any notes for this approval..."
                  value={approveNotes}
                  onChange={(e) => setApproveNotes(e.target.value)}
                />
              </Form.Group>
              <Alert variant="info" className="mt-3">
                <strong>This will:</strong>
                <ul className="mb-0 mt-1">
                  <li>✅ Approve the application</li>
                  <li>📄 Send document submission link to <strong>{selectedApp.email}</strong></li>
                  <li>🔐 Login credentials will be sent AFTER document verification</li>
                </ul>
              </Alert>
              <Alert variant="warning" className="mt-2">
                <small>
                  <strong>Note:</strong> The seller will receive a document submission link. 
                  They will get login credentials only after their documents are verified.
                </small>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApproveModal(false)}>Cancel</Button>
          <Button variant="success" onClick={handleApprove} disabled={actionLoading}>
            {actionLoading ? <Spinner size="sm" className="me-2" /> : <FaCheck className="me-2" />}
            Approve & Send Document Link
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ==================== REJECT MODAL ==================== */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title><FaTimesCircle className="me-2 text-danger" /> Reject Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApp && (
            <>
              <p><strong>Business:</strong> {selectedApp.businessName}</p>
              <p><strong>Applicant:</strong> {selectedApp.fullName}</p>
              <p><strong>Email:</strong> {selectedApp.email}</p>
              <p><strong>Document Status:</strong> {getDocumentStatusBadge(selectedApp.documentStatus)}</p>
              <Form.Group className="mt-3">
                <Form.Label>Rejection Reason <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Please provide a reason for rejection..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mt-3">
                <Form.Label>Additional Notes (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Add any additional notes..."
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                />
              </Form.Group>
              <Alert variant="danger" className="mt-3">
                <strong>This will:</strong>
                <ul className="mb-0 mt-1">
                  <li>❌ Reject the application</li>
                  <li>📧 Send rejection email to <strong>{selectedApp.email}</strong></li>
                </ul>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleReject} disabled={actionLoading}>
            {actionLoading ? <Spinner size="sm" className="me-2" /> : <FaTimes className="me-2" />}
            Reject & Send Email
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SellerApplications;
